import { useState, useEffect } from 'react';

export default function Hero() {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Safe to access window here (client-side only)
    if ((window as any).__deferredInstallPrompt) {
      setInstallPrompt((window as any).__deferredInstallPrompt);
    }

    try {
      setIsInstalled(window.matchMedia('(display-mode: standalone)').matches);
    } catch {
      setIsInstalled(false);
    }

    const onInstallable = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) setInstallPrompt(detail);
    };

    window.addEventListener('pwa-installable', onInstallable);
    window.addEventListener('appinstalled', () => {
      setInstallPrompt(null);
      setIsInstalled(true);
    });

    return () => {
      window.removeEventListener('pwa-installable', onInstallable);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      setIsInstalled(true);
      (window as any).__deferredInstallPrompt = null;
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/8a9fb58f748ca054c3bba3e3b156a481.jpg" 
          alt="Water Damage Restoration in Merced CA - Indoor-Restore Services" 
          className="w-full h-full object-cover object-top"
          // @ts-expect-error fetchpriority is a valid HTML attribute not yet in React types
          fetchpriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full py-20">
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
          Expert Water Damage<br />Restoration in Merced, CA
        </h1>
        <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
          24/7 Emergency Response • Licensed &amp; Insured • Fast &amp; Reliable Service
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a href="tel:2094512288" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer">
            <i className="ri-phone-line mr-2"></i>
            Call Now: (209) 451-2288
          </a>
          <button onClick={scrollToContact} className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer">
            Get Free Estimate
          </button>

          {/* Install App button — only shown when browser install prompt is available */}
          {!isInstalled && installPrompt && (
            <button
              onClick={handleInstall}
              className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/40 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/25 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-download-2-line text-xl"></i>
              </div>
              Install App
            </button>
          )}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">24/7 Emergency</h3>
            <p className="text-white/80 text-sm">Available around the clock for urgent water damage situations</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Licensed &amp; Insured</h3>
            <p className="text-white/80 text-sm">Fully certified professionals you can trust</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-speed-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Fast Response</h3>
            <p className="text-white/80 text-sm">Quick arrival to minimize damage and costs</p>
          </div>
        </div>
      </div>
    </section>
  );
}
